import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import TestimonialCard from '../components/TestimonialCard';
import { useModal } from '../contexts/ModalContext'; // For "Schedule Your Service" CTA

// Placeholder for StarIcon - replace with actual Heroicon or SVG from a shared location if not in TestimonialCard
const StarIcon = ({ filled, className }) => (
  <span className={`inline-block w-6 h-6 ${filled ? 'text-yellow-400' : 'text-gray-100'} ${className}`}>
    ★
  </span>
);

// Placeholder for social media icons - replace with actual SVGs
const GoogleIcon = () => <span className="w-5 h-5 mr-2 text-red-600">G</span>;
const YelpIcon = () => <span className="w-5 h-5 mr-2 text-red-500">Y</span>;

// Placeholder Testimonial Data (Replace with API call)
const placeholderTestimonials = [
  {
    id: 1,
    rating: 5,
    reviewText: 'Pejman and his team were fantastic! They fixed our AC quickly during a heatwave. Professional, courteous, and fairly priced. Highly recommend Quality Air Houston!',
    customerName: 'Sarah P.',
    customerLocation: 'Houston, TX',
    reviewDate: 'June 2024',
    customerAvatar: '/images/avatars/avatar-1.png', // Placeholder
    servicePerformed: 'AC Repair',
  },
  {
    id: 2,
    rating: 5,
    reviewText: 'Great experience with Quality Air Houston for a new furnace installation. The process was smooth, and they explained everything clearly. Our home is much warmer now.',
    customerName: 'John B.',
    customerLocation: 'Sugar Land, TX',
    reviewDate: 'May 2024',
    customerAvatar: '/images/avatars/avatar-2.png', // Placeholder
    servicePerformed: 'New Installation',
  },
  {
    id: 3,
    rating: 5,
    reviewText: 'Scheduled routine maintenance. The technician was on time, very thorough, and gave us some great tips for energy saving. Will definitely use them again.',
    customerName: 'Maria G.',
    customerLocation: 'Katy, TX',
    reviewDate: 'April 2024',
    // customerAvatar: null, // Example of no avatar
    servicePerformed: 'Maintenance',
  },
    {
    id: 4,
    rating: 5,
    reviewText: 'Our commercial HVAC system went down, and Quality Air Houston responded incredibly fast. Pejman diagnosed the issue and got us back up and running in no time. Lifesavers!',
    customerName: 'David L. (ABC Corp)',
    customerLocation: 'Downtown Houston, TX',
    reviewDate: 'June 2024',
    customerAvatar: '/images/avatars/avatar-3.png',
    servicePerformed: 'Commercial HVAC',
  },
  {
    id: 5,
    rating: 5,
    reviewText: 'Good service for an AC tune-up. The technician was knowledgeable. Would appreciate slightly more upfront detail on potential add-on costs, but overall satisfied.',
    customerName: 'Linda H.',
    customerLocation: 'The Woodlands, TX',
    reviewDate: 'May 2024',
    servicePerformed: 'Maintenance',
  },
  {
    id: 6,
    rating: 5,
    reviewText: 'Excellent and honest service. Pejman really knows his stuff and doesn\'t try to upsell you. Fixed a complicated issue that another company couldn\'t. Highly recommend!',
    customerName: 'Robert K.',
    customerLocation: 'Pearland, TX',
    reviewDate: 'April 2024',
    customerAvatar: '/images/avatars/avatar-4.png',
    servicePerformed: 'AC Repair',
  },
];

const TestimonialsPage = () => {
  const { openModal } = useModal(); // For "Schedule Your Service" CTA
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  // const [error, setError] = useState(null); // For API error handling

  // Simulate API call
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setTestimonials(placeholderTestimonials);
      setLoading(false);
    }, 1000); // Simulate network delay
  }, []);

  // TODO: Implement actual API call: GET /api/testimonials/
  // TODO: Implement pagination if there are many reviews
  // TODO: Implement filtering/sorting if desired

  const aggregateRating = testimonials.length > 0 
    ? (testimonials.reduce((acc, curr) => acc + curr.rating, 0) / testimonials.length).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        {/* Basic Spinner - Replace with a nicer one if available */}
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-brand-accent"></div>
        <p className="ml-4 text-lg text-brand-gray">Loading Testimonials...</p>
      </div>
    );
  }

  return (
    <div className="bg-brand-background min-h-screen">
      {/* Hero/Header Section */}
      <header 
        className="py-12 md:py-20 text-center bg-gray-700 relative text-white"
        style={{
          backgroundImage: "url('/images/testimonial-hero.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Hear From Our Happy Houston Customers
          </h1>
          <p className="text-lg max-w-3xl mx-auto mb-6">
            At Quality Air Houston, customer satisfaction is our top priority. We're proud to share the experiences of Houston residents and businesses who have trusted us with their HVAC needs. Read what they have to say about our hardworking team and commitment to quality service.
          </p>
          {testimonials.length > 0 && (
            <div className="flex items-center justify-center text-xl">
              <span className="font-semibold mr-2">Average Rating: {aggregateRating}</span>
              <StarIcon filled className="text-yellow-400" /> 
              <span className="ml-1 text-sm ">({testimonials.length} reviews)</span>
            </div>
          )}
        </div>
      </header>

      {/* Testimonial Display Section */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          {/* Optional Filtering/Sorting UI here */}
          {testimonials.length === 0 && !loading && (
            <div className="text-center py-12">
              <h2 className="text-2xl font-semibold text-brand-gray-dark mb-4">No Reviews Yet!</h2>
              <p className="text-brand-gray mb-6">Be the first to share your experience with Quality Air Houston.</p>
              {/* CTA to leave review could also be prominent here */}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((testimonial) => (
              <TestimonialCard key={testimonial.id} {...testimonial} />
            ))}
          </div>
          {/* Optional Pagination UI here */}
        </div>
      </section>

      {/* Call to Action to Leave a Review Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-bold text-brand-gray-dark mb-4">
            Share Your Experience with Quality Air Houston!
          </h2>
          <p className="text-lg text-brand-gray mb-8">
            We value your feedback and would love to hear about your experience with our services. Your review helps us improve and lets other Houston residents know what to expect.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <a 
              href="https://g.page/r/CYe48lNzgX1sEAE/review"
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center bg-white hover:bg-gray-100 text-gray-700 font-semibold py-3 px-6 rounded-lg shadow-md transition duration-300 ease-in-out border border-gray-300 w-full sm:w-auto"
            >
              <GoogleIcon /> Leave a Review on Google
            </a>
            <a 
              href="#" // Replace with actual Yelp Review link
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center bg-white hover:bg-gray-100 text-gray-700 font-semibold py-3 px-6 rounded-lg shadow-md transition duration-300 ease-in-out border border-gray-300 w-full sm:w-auto"
            >
              <YelpIcon /> Review Us on Yelp
            </a>
          </div>
        </div>
      </section>

      {/* Overall Page CTA (Bottom of page, above footer) */}
      <section className="py-16 md:py-20 bg-brand-green-light">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-brand-gray-dark mb-4">
            Ready to Experience 5-Star HVAC Service in Houston?
          </h2>
          <p className="text-lg text-brand-gray mb-8 max-w-2xl mx-auto">
            Our satisfied customers speak for themselves. Contact us today to get the quality service you deserve.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <button
              onClick={() => openModal('scheduleService')}
              className="bg-brand-accent hover:bg-brand-accent-dark text-white font-semibold py-3 px-8 rounded-lg shadow-lg transition duration-300 ease-in-out text-lg"
            >
              Schedule Your Service
            </button>
            <RouterLink
              to="/services"
              className="bg-brand-green hover:bg-brand-green-dark text-white font-semibold py-3 px-8 rounded-lg shadow-lg transition duration-300 ease-in-out text-lg"
            >
              View Our Services
            </RouterLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TestimonialsPage; 